import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Button, Form, Spinner, Badge, ListGroup, Alert, Modal } from 'react-bootstrap';

const ModalSolicitarVacaciones = ({
    show,
    onHide,
    setSelectedDates,
    selectedDaysList,
    setSelectedDaysList,
    calculoVacaciones,
    requestVacation,
    saving
}) => {
    const [dateRange, setDateRange] = useState({
        startDate: '',
        endDate: ''
    });
    const [totalDaysSelected, setTotalDaysSelected] = useState(0);
    // NUEVO ESTADO: Guarda el motivo escrito dentro del modal intermedio
    const [motivo, setMotivo] = useState('');
    const [validacionPeriodo, setValidacionPeriodo] = useState({
        diasDelPeriodoActivo: 0,
        diasHistoricos: 0,
        diasDisponiblesHistoricos: 0,
        diasDisponiblesPeriodoActivo: 0,
        advertencia: null,
        error: null
    });

    // Limpiar el motivo cuando el modal se cierra o se abre vacío
    useEffect(() => {
        if (!show) {
            setMotivo('');
        }
    }, [show]);

    /**
     * Obtiene el período activo de las vacaciones
     */
    const obtenerPeriodoActivo = () => {
        if (!calculoVacaciones.desglosePorAnio || calculoVacaciones.desglosePorAnio.length === 0) {
            return null;
        }
        return calculoVacaciones.desglosePorAnio.find(p => p.bPeriodoActivo === true);
    };

    /**
     * Obtiene los períodos completados con días disponibles
     */
    const obtenerPeriodosHistoricos = () => {
        if (!calculoVacaciones.desglosePorAnio || calculoVacaciones.desglosePorAnio.length === 0) {
            return [];
        }
        return calculoVacaciones.desglosePorAnio.filter(p =>
            p.bPeriodoCompletado === true && p.iDiasDisponibles > 0
        );
    };

    /**
     * Verifica si una fecha está dentro de un período
     */
    const fechaEstaEnPeriodo = (fechaString, periodo) => {
        if (!periodo || !periodo.dFechaInicio || !periodo.dFechaFin) {
            return false;
        }

        const fecha = new Date(fechaString + 'T00:00:00');
        const inicio = new Date(periodo.dFechaInicio + 'T00:00:00');
        const fin = new Date(periodo.dFechaFin + 'T00:00:00');

        return fecha >= inicio && fecha <= fin;
    };

    /**
     * Valida las fechas seleccionadas contra los períodos
     */
    const validarFechasContraPeriodos = (diasSeleccionados) => {
        if (!diasSeleccionados || diasSeleccionados.length === 0) {
            setValidacionPeriodo({
                diasDelPeriodoActivo: 0,
                diasHistoricos: 0,
                diasDisponiblesHistoricos: 0,
                diasDisponiblesPeriodoActivo: 0,
                advertencia: null,
                error: null
            });
            return;
        }

        const periodoActivo = obtenerPeriodoActivo();
        const periodosHistoricos = obtenerPeriodosHistoricos();

        // Calcular días disponibles por tipo
        const diasDisponiblesHistoricos = periodosHistoricos.reduce((sum, p) => sum + (p.iDiasDisponibles || 0), 0);
        const diasDisponiblesPeriodoActivo = periodoActivo ? (periodoActivo.iDiasDisponibles || 0) : 0;

        let diasEnPeriodoActivo = 0;
        let diasFueraDePeriodoActivo = 0;

        // Clasificar cada día seleccionado
        diasSeleccionados.forEach(dia => {
            if (periodoActivo && fechaEstaEnPeriodo(dia.dateString, periodoActivo)) {
                diasEnPeriodoActivo++;
            } else {
                diasFueraDePeriodoActivo++;
            }
        });

        let advertencia = null;
        let error = null;

        //Todos los días están en el período activo
        if (diasEnPeriodoActivo > 0 && diasFueraDePeriodoActivo === 0) {
            if (periodoActivo && !periodoActivo.bPeriodoCompletado) {
                advertencia = {
                    tipo: 'warning',
                    icono: 'exclamation-triangle',
                    titulo: '⚠️ Días del Período Actual',
                    mensaje: `Estás solicitando ${diasEnPeriodoActivo} día${diasEnPeriodoActivo > 1 ? 's' : ''} del período activo (${periodoActivo.vDescripcionAnio}).`,
                    detalles: [
                        'Estos días solo pueden usarse después de completar el período.',
                        'Para casos excepcionales, debes obtener autorización de tu supervisor.',
                        `Tienes ${diasDisponiblesHistoricos} día${diasDisponiblesHistoricos > 1 ? 's' : ''} de períodos anteriores disponibles para uso inmediato.`
                    ]
                };
            }
        }

        //Mezcla de días históricos y del período activo
        if (diasEnPeriodoActivo > 0 && diasFueraDePeriodoActivo > 0) {
            advertencia = {
                tipo: 'info',
                icono: 'info-circle',
                titulo: '📊 Días Mixtos Seleccionados',
                mensaje: `Has seleccionado días de diferentes períodos:`,
                detalles: [
                    `✓ ${diasFueraDePeriodoActivo} día${diasFueraDePeriodoActivo > 1 ? 's' : ''} de períodos completados (disponibles)`,
                    `⚠ ${diasEnPeriodoActivo} día${diasEnPeriodoActivo > 1 ? 's' : ''} del período activo (requieren autorización)`
                ]
            };
        }

        //Días insuficientes
        const totalDisponible = diasDisponiblesHistoricos + diasDisponiblesPeriodoActivo;
        if (diasSeleccionados.length > totalDisponible) {
            error = {
                tipo: 'danger',
                icono: 'times-circle',
                titulo: '❌ Días Insuficientes',
                mensaje: `Has seleccionado ${diasSeleccionados.length} días, pero solo tienes ${totalDisponible} disponibles.`,
                detalles: [
                    `Días de períodos completados: ${diasDisponiblesHistoricos}`,
                    `Días del período actual: ${diasDisponiblesPeriodoActivo}`
                ]
            };
        }

        //Usando más días históricos de los disponibles
        if (diasFueraDePeriodoActivo > diasDisponiblesHistoricos) {
            error = {
                tipo: 'danger',
                icono: 'times-circle',
                titulo: '❌ Días Históricos Insuficientes',
                mensaje: `Intentas usar ${diasFueraDePeriodoActivo} días, pero solo tienes ${diasDisponiblesHistoricos} días de períodos completados.`,
                detalles: [
                    'Ajusta la selección para que coincida con tus días disponibles.',
                    `Días del período actual disponibles: ${diasDisponiblesPeriodoActivo}`
                ]
            };
        }

        setValidacionPeriodo({
            diasDelPeriodoActivo: diasEnPeriodoActivo,
            diasHistoricos: diasFueraDePeriodoActivo,
            diasDisponiblesHistoricos,
            diasDisponiblesPeriodoActivo,
            advertencia,
            error
        });
    };

    /**
     * Días festivos oficiales en México con descanso obligatorio
     * Según la Ley Federal del Trabajo (Artículo 74)
     */
    const getFeriadosOficiales = (year) => {
        return {
            [`${year}-01-01`]: { name: 'Año Nuevo', type: 'fijo', obligatorio: true },
            [`${year}-05-01`]: { name: 'Día del Trabajo', type: 'fijo', obligatorio: true },
            [`${year}-09-16`]: { name: 'Día de la Independencia', type: 'fijo', obligatorio: true },
            [`${year}-12-25`]: { name: 'Navidad', type: 'fijo', obligatorio: true },
            ...getFirstMondayFeriados(year),
            ...getFeriadosEspeciales(year)
        };
    };

    /**
     * Días festivos especiales (no obligatorios por ley)
     */
    const getFeriadosEspeciales = (year) => {
        const feriados = {};

        const juevesSanto = getJuevesSanto(year);
        feriados[juevesSanto] = { name: 'Jueves Santo', type: 'religioso', obligatorio: false };

        const viernesSanto = getViernesSanto(year);
        feriados[viernesSanto] = { name: 'Viernes Santo', type: 'religioso', obligatorio: false };

        feriados[`${year}-05-10`] = { name: 'Día de las Madres', type: 'conmemorativo', obligatorio: false };

        return feriados;
    };

    const getJuevesSanto = (year) => {
        const domingoPascua = calcularDomingoPascua(year);
        const juevesSanto = new Date(domingoPascua);
        juevesSanto.setDate(juevesSanto.getDate() - 3);
        return formatDateString(juevesSanto);
    };

    const getViernesSanto = (year) => {
        const domingoPascua = calcularDomingoPascua(year);
        const viernesSanto = new Date(domingoPascua);
        viernesSanto.setDate(viernesSanto.getDate() - 2);
        return formatDateString(viernesSanto);
    };

    const calcularDomingoPascua = (year) => {
        const a = year % 19;
        const b = Math.floor(year / 100);
        const c = year % 100;
        const d = Math.floor(b / 4);
        const e = b % 4;
        const f = Math.floor((b + 8) / 25);
        const g = Math.floor((b - f + 1) / 3);
        const h = (19 * a + b - d - g + 15) % 30;
        const i = Math.floor(c / 4);
        const k = c % 4;
        const l = (32 + 2 * e + 2 * i - h - k) % 7;
        const m = Math.floor((a + 11 * h + 22 * l) / 451);
        const month = Math.floor((h + l - 7 * m + 114) / 31);
        const day = ((h + l - 7 * m + 114) % 31) + 1;
        return new Date(year, month - 1, day);
    };

    const getFirstMondayFeriados = (year) => {
        const feriados = {};

        const primerLunesFebrero = getPrimerLunes(year, 1);
        if (primerLunesFebrero !== `${year}-02-05`) {
            feriados[primerLunesFebrero] = { name: 'Día de la Constitución (Lunes)', type: 'movible', obligatorio: true };
        }

        const tercerLunesMarzo = getTercerLunes(year, 2);
        if (tercerLunesMarzo !== `${year}-03-21`) {
            feriados[tercerLunesMarzo] = { name: 'Natalicio de Benito Juárez (Lunes)', type: 'movible', obligatorio: true };
        }

        const tercerLunesNoviembre = getTercerLunes(year, 10);
        if (tercerLunesNoviembre !== `${year}-11-20`) {
            feriados[tercerLunesNoviembre] = { name: 'Revolución Mexicana (Lunes)', type: 'movible', obligatorio: true };
        }

        return feriados;
    };

    const getPrimerLunes = (year, month) => {
        let date = new Date(year, month, 1);
        while (date.getDay() !== 1) {
            date.setDate(date.getDate() + 1);
        }
        return formatDateString(date);
    };

    const getTercerLunes = (year, month) => {
        let date = new Date(year, month, 1);
        let lunesCount = 0;
        while (lunesCount < 3) {
            if (date.getDay() === 1) {
                lunesCount++;
                if (lunesCount === 3) break;
            }
            date.setDate(date.getDate() + 1);
        }
        return formatDateString(date);
    };

    const formatDateString = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const esFeriadoOficial = (dateString) => {
        const year = new Date(dateString).getFullYear();
        const feriados = getFeriadosOficiales(year);
        return feriados[dateString] || null;
    };

    useEffect(() => {
        validarFechasContraPeriodos(selectedDaysList);
    }, [selectedDaysList, calculoVacaciones]);

    const createLocalDate = (dateString) => {
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day);
    };

    const calculateDaysBetween = (start, end, excludeWeekends = false) => {
        if (!start || !end) return [];

        const startDate = createLocalDate(start);
        const endDate = createLocalDate(end);
        const days = [];

        if (endDate < startDate) return [];

        let currentDate = new Date(startDate);

        while (currentDate <= endDate) {
            const dayOfWeek = currentDate.getDay();

            if (!excludeWeekends || (dayOfWeek !== 0 && dayOfWeek !== 6)) {
                const year = currentDate.getFullYear();
                const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                const day = String(currentDate.getDate()).padStart(2, '0');
                const dateString = `${year}-${month}-${day}`;

                const feriadoInfo = esFeriadoOficial(dateString);
                const periodoActivo = obtenerPeriodoActivo();
                const estaEnPeriodoActivo = periodoActivo && fechaEstaEnPeriodo(dateString, periodoActivo);

                days.push({
                    date: new Date(currentDate),
                    dateString: dateString,
                    dayName: currentDate.toLocaleDateString('es-MX', { weekday: 'long' }),
                    dayNumber: currentDate.getDate(),
                    month: currentDate.toLocaleDateString('es-MX', { month: 'long' }),
                    year: currentDate.getFullYear(),
                    isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
                    isFeriado: feriadoInfo !== null,
                    feriadoName: feriadoInfo?.name || null,
                    estaEnPeriodoActivo: estaEnPeriodoActivo
                });
            }

            currentDate.setDate(currentDate.getDate() + 1);
        }

        return days;
    };

    const handleDateRangeChange = (e) => {
        const { name, value } = e.target;
        const newDateRange = {
            ...dateRange,
            [name]: value
        };
        setDateRange(newDateRange);

        if (newDateRange.startDate && newDateRange.endDate) {
            const days = calculateDaysBetween(newDateRange.startDate, newDateRange.endDate, false);
            setSelectedDaysList(days);
            setTotalDaysSelected(days.length);

            setSelectedDates({
                startDate: newDateRange.startDate,
                endDate: newDateRange.endDate
            });
        } else {
            setSelectedDaysList([]);
            setTotalDaysSelected(0);
        }
    };

    const removeDayFromList = (dateString) => {
        const updatedList = selectedDaysList.filter(day => day.dateString !== dateString);
        setSelectedDaysList(updatedList);
        setTotalDaysSelected(updatedList.length);

        if (updatedList.length === 0) {
            setDateRange({ startDate: '', endDate: '' });
            setSelectedDates({ startDate: '', endDate: '' });
        } else {
            const dates = updatedList.map(d => d.dateString).sort();
            const newStartDate = dates[0];
            const newEndDate = dates[dates.length - 1];

            setDateRange({ startDate: newStartDate, endDate: newEndDate });
            setSelectedDates({ startDate: newStartDate, endDate: newEndDate });
        }
    };

    const clearSelection = () => {
        setDateRange({ startDate: '', endDate: '' });
        setSelectedDaysList([]);
        setTotalDaysSelected(0);
        setSelectedDates({ startDate: '', endDate: '' });
        setMotivo('');
    };

    const canSubmit = () => {
        return (
            selectedDaysList.length > 0 &&
            !validacionPeriodo.error &&
            !saving
        );
    };

    const formatDisplayDate = (day) => {
        const dayName = day.dayName.charAt(0).toUpperCase() + day.dayName.slice(1);
        const month = day.month.charAt(0).toUpperCase() + day.month.slice(1);
        return `${dayName}, ${day.dayNumber} de ${month} ${day.year}`;
    };

    // FUNCIÓN DE SUBMIT INTERNA MODIFICADA
    const handleEnviarSolicitud = () => {
        if (!canSubmit()) return;
        // Lanzamos el prop mandando el estado local "motivo" que se inyectará al Banner
        requestVacation(motivo);
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title className="fs-0">
                    Solicitar Vacaciones
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <Alert variant="info" className="py-2 mb-2">
                        <div className="d-flex justify-content-between align-items-center">
                            <small>
                                <FontAwesomeIcon icon="calendar-check" className="me-1" />
                                <strong>Días Disponibles para Uso Inmediato:</strong>
                            </small>
                            <Badge bg="success" className="ms-2">{validacionPeriodo.diasDisponiblesHistoricos} días</Badge>
                        </div>
                    </Alert>

                    {validacionPeriodo.diasDisponiblesPeriodoActivo > 0 && (
                        <Alert variant="warning" className="py-2 mb-0">
                            <div className="d-flex justify-content-between align-items-center">
                                <small>
                                    <FontAwesomeIcon icon="clock" className="me-1" />
                                    <strong>Días del Período Actual:</strong>
                                </small>
                                <Badge bg="warning" className="ms-2">{validacionPeriodo.diasDisponiblesPeriodoActivo} días</Badge>
                            </div>
                            <small className="text-muted d-block mt-1" style={{ fontSize: '0.75rem' }}>
                                Disponibles al finalizar el período o con autorización
                            </small>
                        </Alert>
                    )}
                </div>

                <div className="mb-3">
                    <Form.Label className="fw-bold small">Selecciona el periodo de vacaciones</Form.Label>
                    <div className="row g-2">
                        <div className="col md-6">
                            <Form.Group>
                                <Form.Label className="small text-muted">Fecha de inicio</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="startDate"
                                    value={dateRange.startDate}
                                    onChange={handleDateRangeChange}
                                    disabled={saving}
                                />
                            </Form.Group>
                        </div>
                        <div className="col md-6">
                            <Form.Group>
                                <Form.Label className="small text-muted">Fecha de fin</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="endDate"
                                    value={dateRange.endDate}
                                    onChange={handleDateRangeChange}
                                    min={dateRange.startDate}
                                    disabled={saving || !dateRange.startDate}
                                />
                            </Form.Group>
                        </div>
                    </div>
                </div>

                {validacionPeriodo.error && (
                    <Alert variant={validacionPeriodo.error.tipo} className="mb-3">
                        <div className="d-flex align-items-start">
                            <FontAwesomeIcon icon={validacionPeriodo.error.icono} className="me-2 mt-1" />
                            <div className="flex-grow-1">
                                <strong className="d-block mb-1">{validacionPeriodo.error.titulo}</strong>
                                <small className="d-block mb-2">{validacionPeriodo.error.mensaje}</small>
                                {validacionPeriodo.error.detalles && (
                                    <ul className="mb-0 ps-3" style={{ fontSize: '0.85rem' }}>
                                        {validacionPeriodo.error.detalles.map((detalle, idx) => (
                                            <li key={idx}>{detalle}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </Alert>
                )}

                {validacionPeriodo.advertencia && !validacionPeriodo.error && (
                    <Alert variant={validacionPeriodo.advertencia.tipo} className="mb-3">
                        <div className="d-flex align-items-start">
                            <FontAwesomeIcon icon={validacionPeriodo.advertencia.icono} className="me-2 mt-1" />
                            <div className="flex-grow-1">
                                <strong className="d-block mb-1">{validacionPeriodo.advertencia.titulo}</strong>
                                <small className="d-block mb-2">{validacionPeriodo.advertencia.mensaje}</small>
                                {validacionPeriodo.advertencia.detalles && (
                                    <ul className="mb-0 ps-3" style={{ fontSize: '0.85rem' }}>
                                        {validacionPeriodo.advertencia.detalles.map((detalle, idx) => (
                                            <li key={idx}>{detalle}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </Alert>
                )}

                {totalDaysSelected > 0 && (
                    <div className="mb-3 p-2 bg-light rounded border">
                        <div className="row g-2 text-center">
                            <div className="col-6">
                                <div className="p-2">
                                    <FontAwesomeIcon icon="check-circle" className="text-success d-block mb-1" />
                                    <strong className="d-block text-success">{validacionPeriodo.diasHistoricos}</strong>
                                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>Días Disponibles</small>
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="p-2">
                                    <FontAwesomeIcon icon="exclamation-circle" className="text-warning d-block mb-1" />
                                    <strong className="d-block text-warning">{validacionPeriodo.diasDelPeriodoActivo}</strong>
                                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>Período Actual</small>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {selectedDaysList.length > 0 && (
                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <Form.Label className="fw-bold small mb-0">
                                Días a solicitar ({totalDaysSelected})
                            </Form.Label>
                            <Button
                                variant="link"
                                size="sm"
                                className="text-danger p-0"
                                onClick={clearSelection}
                                disabled={saving}
                            >
                                <FontAwesomeIcon icon="times" className="me-1" />
                                Limpiar todo
                            </Button>
                        </div>

                        <div
                            className="border rounded"
                            style={{
                                maxHeight: '180px',
                                overflowY: 'auto',
                                backgroundColor: '#f8f9fa'
                            }}
                        >
                            <ListGroup variant="flush">
                                {selectedDaysList.map((day, index) => (
                                    <ListGroup.Item
                                        key={day.dateString}
                                        className="d-flex justify-content-between align-items-center py-2 px-3"
                                        style={{ backgroundColor: 'transparent' }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <Badge
                                                bg={
                                                    day.isWeekend ? 'secondary' :
                                                        day.isFeriado ? (
                                                            esFeriadoOficial(day.dateString)?.obligatorio ? 'danger' : 'warning'
                                                        ) :
                                                            day.estaEnPeriodoActivo ? 'warning' : 'success'
                                                }
                                                text="white"
                                                className="me-2"
                                                style={{ minWidth: '30px' }}
                                            >
                                                {index + 1}
                                            </Badge>
                                            <div className="d-flex flex-column">
                                                <span className="small">
                                                    {formatDisplayDate(day)}
                                                </span>
                                                <div className="d-flex gap-1 mt-1 flex-wrap">
                                                    {day.estaEnPeriodoActivo && !day.isWeekend && !day.isFeriado && (
                                                        <Badge bg="warning" style={{ fontSize: '0.65rem' }}>
                                                            ⚠️ Período Actual
                                                        </Badge>
                                                    )}
                                                    {!day.estaEnPeriodoActivo && !day.isWeekend && !day.isFeriado && (
                                                        <Badge bg="success" style={{ fontSize: '0.65rem' }}>
                                                            ✓ Disponible
                                                        </Badge>
                                                    )}
                                                    {day.isWeekend && (
                                                        <Badge bg="secondary" style={{ fontSize: '0.65rem' }}>
                                                            Fin de semana
                                                        </Badge>
                                                    )}
                                                    {day.isFeriado && (
                                                        <Badge
                                                            bg={esFeriadoOficial(day.dateString)?.obligatorio ? 'danger' : 'warning'}
                                                            style={{ fontSize: '0.65rem' }}
                                                        >
                                                            {esFeriadoOficial(day.dateString)?.obligatorio ? '🎉' : '🌟'} {day.feriadoName}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="text-danger p-0"
                                            onClick={() => removeDayFromList(day.dateString)}
                                            disabled={saving}
                                            title="Eliminar este día"
                                        >
                                            <FontAwesomeIcon icon="trash-alt" />
                                        </Button>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </div>
                    </div>
                )}

                {/* ADAPTACIÓN AGREGADA: Cuadro de texto para capturar el Motivo dentro del Modal */}
                <Form.Group className="mb-2">
                    <Form.Label className="fw-bold small mb-1">
                        Motivo <span className="text-muted fw-normal small">(opcional)</span>
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Explica brevemente el motivo de tu solicitud..."
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                        disabled={saving}
                        style={{ resize: 'none', fontSize: '0.85rem' }}
                    />
                </Form.Group>

            </Modal.Body>
            <Modal.Footer className="d-flex flex-column gap-2 border-top-0 pt-0">
                <div className="d-grid gap-2 w-100">
                    <Button
                        variant="primary"
                        onClick={handleEnviarSolicitud} // Cambiado a la función interna que inyecta el motivo
                        disabled={!canSubmit()}
                        size="lg"
                    >
                        {saving ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Enviando solicitud...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon="paper-plane" className="me-2" />
                                Enviar Solicitud ({totalDaysSelected} d&iacute;a{totalDaysSelected !== 1 ? 's' : ''})
                            </>
                        )}
                    </Button>
                </div>
                {selectedDaysList.length > 0 && !saving && canSubmit() && (
                    <small className="text-center text-muted">
                        <FontAwesomeIcon icon="info-circle" className="me-1" />
                        Revisa los d&iacute;as seleccionados antes de enviar
                    </small>
                )}
            </Modal.Footer>
        </Modal>
    );
};

ModalSolicitarVacaciones.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    selectedDates: PropTypes.shape({
        startDate: PropTypes.string.isRequired,
        endDate: PropTypes.string.isRequired
    }).isRequired,
    setSelectedDates: PropTypes.func.isRequired,
    selectedDaysList: PropTypes.array.isRequired,
    setSelectedDaysList: PropTypes.func.isRequired, // Corregido: Limpiamos la declaración duplicada en PropTypes
    calculoVacaciones: PropTypes.shape({
        diasDisponibles: PropTypes.number.isRequired,
        desglosePorAnio: PropTypes.array
    }).isRequired,
    requestVacation: PropTypes.func.isRequired,
    saving: PropTypes.bool.isRequired
};

export default ModalSolicitarVacaciones;